import {
  Activity as ActivityIcon,
  Download,
  Filter as FilterIcon,
  RefreshCw,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { type AuditEntry, useAuditActions, useAuditCsv, useAuditList } from '../api/useAudit.js';
import { useRoster } from '../api/useRoster.js';
import { EmptyState } from '../components/EmptyState.js';
import { RoleGate } from '../components/RoleGate.js';
import { cn } from '../lib/cn.js';
import { formatDateTime, formatRelative } from '../lib/format.js';

// Admin activity log. Filters by action, actor, time range; paginated-ish via top-N
// slice with "load older" via cursor (not implemented yet — first pass uses limit=500).
//
// Tonight-of-incident style questions it answers:
//   • Who signed in, and when?
//   • Who acknowledged that alarm?
//   • Who changed the Nursing team last week?
//   • Show everything that worker X did yesterday.

const DEFAULT_LIMIT = 200;

const HUMAN: Record<string, string> = {
  'session.signed_in': 'signed in',
  'session.ended': 'signed out',
  'session.removed': 'session removed',
  'session.revoked': 'session revoked',
  'alarm.triggered': 'triggered alarm',
  'alarm.acknowledged': 'acknowledged alarm',
  'alarm.resolved': 'resolved alarm',
  'alarm.false_alarm': 'flagged false alarm',
  'check_in.started': 'started check-in',
  'check_in.completed': 'completed check-in',
  'check_in.cancelled': 'cancelled check-in',
  'check_in.extended': 'extended check-in',
  'broadcast.sent': 'sent broadcast',
  'team.created': 'created team',
  'team.updated': 'updated team',
  'team.deleted': 'deleted team',
  'team.member_added': 'added team members',
  'team.member_removed': 'removed team member',
  'welfare_check.requested': 'requested welfare check',
};

export default function Activity() {
  const [actions, setActions] = useState<string[]>([]);
  const [actorIds, setActorIds] = useState<string[]>([]);
  const [from, setFrom] = useState<string>(defaultFrom());
  const [to, setTo] = useState<string>('');
  const [showFilters, setShowFilters] = useState(true);

  const params = { action: actions, actor_id: actorIds, from, to, limit: DEFAULT_LIMIT };
  const query = useAuditList(params);
  const actionsQ = useAuditActions();
  const roster = useRoster();
  const download = useAuditCsv();

  const entries = query.data?.entries ?? [];

  return (
    <RoleGate allow={['admin']}>
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <span className="eyebrow">Audit</span>
            <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
              Activity
            </h1>
            <p className="text-sm text-ink-faint">
              Admin audit log. Sign-ins, alarm actions, check-ins, broadcasts, team changes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface-1 px-3 py-1.5 text-sm font-semibold text-ink-dim hover:border-line-strong hover:bg-surface-3 hover:text-ink"
            >
              <FilterIcon className="h-4 w-4" />
              Filters
            </button>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface-1 px-3 py-1.5 text-sm font-semibold text-ink-dim hover:border-line-strong hover:bg-surface-3 hover:text-ink"
            >
              <RefreshCw className={cn('h-4 w-4', query.isFetching && 'animate-spin')} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => download(params)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white shadow-glow-primary hover:bg-brand-purple/90"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {showFilters && (
          <FiltersPanel
            allActions={actionsQ.data?.actions ?? []}
            allActors={(roster.data?.members ?? []).map((m) => ({
              id: m.user_id,
              name: m.display_name,
              email: m.email,
            }))}
            actions={actions}
            actorIds={actorIds}
            from={from}
            to={to}
            onChange={(next) => {
              if (next.actions !== undefined) setActions(next.actions);
              if (next.actorIds !== undefined) setActorIds(next.actorIds);
              if (next.from !== undefined) setFrom(next.from);
              if (next.to !== undefined) setTo(next.to);
            }}
            onClear={() => {
              setActions([]);
              setActorIds([]);
              setFrom('');
              setTo('');
            }}
          />
        )}

        {query.isError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Failed to load activity: {(query.error as Error).message}
          </div>
        )}

        {!query.isLoading && entries.length === 0 && (
          <EmptyState
            icon={ActivityIcon}
            title="No activity for these filters"
            description="Try a wider date range or clear the filters."
          />
        )}

        {entries.length > 0 && (
          <div className="panel">
            <table className="min-w-full divide-y divide-line">
              <thead className="bg-surface-3">
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                  <Th className="text-right">Details</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {entries.map((e) => (
                  <Row key={e.id} entry={e} />
                ))}
              </tbody>
            </table>
            {query.data?.next_cursor && (
              <div className="border-t border-line bg-surface-3 px-4 py-2 text-center text-xs text-ink-faint">
                Showing the most recent {entries.length} events. Export CSV for the full window.
              </div>
            )}
          </div>
        )}
      </div>
    </RoleGate>
  );
}

function Row({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const action = HUMAN[entry.action] ?? entry.action;
  return (
    <>
      <tr className="hover:bg-surface-3">
        <Td className="whitespace-nowrap text-xs text-ink-faint">
          <div>{formatRelative(entry.occurred_at)}</div>
          <div className="text-[10px] text-ink-mute">{formatDateTime(entry.occurred_at)}</div>
        </Td>
        <Td>
          {entry.actor_display_name ? (
            <div>
              <div className="text-sm font-medium text-ink">{entry.actor_display_name}</div>
              <div className="text-xs text-ink-faint">{entry.actor_email}</div>
            </div>
          ) : (
            <span className="text-xs italic text-ink-mute">system</span>
          )}
        </Td>
        <Td>
          <span className="inline-flex items-center rounded-full bg-surface-3 px-2 py-0.5 text-xs font-medium text-ink-dim ring-1 ring-inset ring-gray-200">
            {action}
          </span>
        </Td>
        <Td className="text-xs text-ink-faint">
          {entry.target_type ? (
            <div>
              <div className="capitalize">{entry.target_type.replace('_', ' ')}</div>
              {entry.target_id && (
                <div className="font-mono text-[10px] text-ink-mute">
                  {entry.target_id.slice(0, 8)}…
                </div>
              )}
            </div>
          ) : (
            '—'
          )}
        </Td>
        <Td className="text-right">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-brand-purple hover:underline"
          >
            {open ? 'Hide' : 'View'}
          </button>
        </Td>
      </tr>
      {open && (
        <tr className="bg-surface-3">
          <td colSpan={5} className="px-4 py-3">
            <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-white p-3 font-mono text-[11px] text-ink-dim ring-1 ring-gray-100">
              {JSON.stringify(entry.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function FiltersPanel({
  allActions,
  allActors,
  actions,
  actorIds,
  from,
  to,
  onChange,
  onClear,
}: {
  allActions: string[];
  allActors: { id: string; name: string; email: string }[];
  actions: string[];
  actorIds: string[];
  from: string;
  to: string;
  onChange: (
    next: Partial<{ actions: string[]; actorIds: string[]; from: string; to: string }>,
  ) => void;
  onClear: () => void;
}) {
  const actionSet = useMemo(() => new Set(actions), [actions]);
  const actorSet = useMemo(() => new Set(actorIds), [actorIds]);

  return (
    <div className="mb-4 rounded-lg border border-line bg-white p-4 shadow-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wider text-ink-faint">Filters</div>
        {(actions.length > 0 || actorIds.length > 0 || from || to) && (
          <button type="button" onClick={onClear} className="text-xs text-ink-faint hover:text-ink">
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-[11px] font-medium text-ink-faint">Actions</label>
          <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto">
            {allActions.length === 0 ? (
              <span className="text-xs text-ink-mute">No events yet.</span>
            ) : (
              allActions.map((a) => {
                const active = actionSet.has(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => {
                      const next = new Set(actionSet);
                      if (active) next.delete(a);
                      else next.add(a);
                      onChange({ actions: [...next] });
                    }}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
                      active
                        ? 'border-brand-purple bg-brand-purple text-white'
                        : 'border-line bg-white text-ink-dim hover:border-line-strong',
                    )}
                  >
                    {HUMAN[a] ?? a}
                    {active && <X className="h-3 w-3" />}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div>
          <label htmlFor="af" className="mb-1 block text-[11px] font-medium text-ink-faint">
            From
          </label>
          <input
            id="af"
            type="datetime-local"
            value={from}
            onChange={(e) =>
              onChange({ from: e.target.value ? new Date(e.target.value).toISOString() : '' })
            }
            className="w-full rounded-md border border-line-strong px-2.5 py-1.5 text-xs focus:border-brand-purple focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="at" className="mb-1 block text-[11px] font-medium text-ink-faint">
            To
          </label>
          <input
            id="at"
            type="datetime-local"
            value={to}
            onChange={(e) =>
              onChange({ to: e.target.value ? new Date(e.target.value).toISOString() : '' })
            }
            className="w-full rounded-md border border-line-strong px-2.5 py-1.5 text-xs focus:border-brand-purple focus:outline-none"
          />
        </div>

        <div className="sm:col-span-4">
          <label className="mb-1 block text-[11px] font-medium text-ink-faint">Actors</label>
          <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto">
            {allActors.length === 0 ? (
              <span className="text-xs text-ink-mute">No actors.</span>
            ) : (
              allActors.map((a) => {
                const active = actorSet.has(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      const next = new Set(actorSet);
                      if (active) next.delete(a.id);
                      else next.add(a.id);
                      onChange({ actorIds: [...next] });
                    }}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
                      active
                        ? 'border-brand-purple bg-brand-purple text-white'
                        : 'border-line bg-white text-ink-dim hover:border-line-strong',
                    )}
                  >
                    {a.name}
                    {active && <X className="h-3 w-3" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}

function defaultFrom(): string {
  // Last 7 days.
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
}
